// Which TASK each line of the Job Costs statement belongs to — the second
// axis, beside the topic. Same discipline as cost-topics.js: pure arithmetic
// on plain objects, no DOM, no network, and the one rule that makes it safe:
// assigning a cost to a task REGROUPS money, it never CHANGES money.
// `rollUpTasks` returns `grand` as a sum over the items so the caller can
// assert that, whatever the assignments say.
//
// Resolution order, first answer wins:
//   1. a typed cost's own task_id column (source 'entry')
//   2. the item-level override        ('delivery_item', for split orders)
//   3. the per-item rule              ('other_item': one answer per catalogue
//      item per job, for every delivery of it, past and still to come — a
//      delivery line only; it survives a catalogue edit that changes the
//      item's key, adoptItemRules). It outranks the whole-order filing below
//      (owner's decision, 2026-10-03): "picked once per item" has to reach
//      the deliveries filed order by order before the rule existed.
//   4. the line-level override        (the whole order / trade / crew / row)
//   5. the derived default            (recipe lines: a quantity contract
//      line naming the same production item claims it with zero clicks;
//      a delivery line, 2026-11-02: the task whose recipe names its catalogue
//      item, else the job's only task — itemTaskDefaults)
//   6. General (null) — visible, never hidden.
// Sales tax is the one line that answers through ANOTHER line: each part of it
// is filed where the material it was charged on is filed (steps 2–5 for that
// material), and only then under the tax line's own assignment (step 4).
// Every answer is validated against the live task list; a stale assignment
// (deleted task, renamed trade) degrades to General with its money intact.

import { itemRef } from './cost-topics.js';

/** The ref of one item inside an order: '<delivery_id>#<stable item key>'. */
export function deliveryItemRef(deliveryId, itemKey) {
  return String(deliveryId) + '#' + String(itemKey);
}

/**
 * Which ITEM one delivery line is, across every delivery of a job — the key of
 * its row on Cost Recipes' "Other item deliveries" list (cost-recipe.js
 * cutDeliveries groups by this very function) and the `source_ref` of the
 * per-item rule filed there (source 'other_item').
 *
 * It is the catalogue row, not the line's own key: one item reaches a delivery
 * as a built-in line ('<cat>|<type_key>') or as an extra ('extra|<the name
 * frozen that day>'), so one row can stand for several line keys — and each of
 * them, on any delivery, past or still to come, answers with the same key
 * here. An older extra whose name fits several catalogue rows has no single
 * row to name; it is keyed by name and unit, exactly as the list groups it.
 * `e` is an estimate entry: { ids, label, unit }.
 */
export function otherItemKey(e) {
  const ids = (e && e.ids) || [];
  return ids.length === 1 ? 'id:' + ids[0] : _nameUnitKey(e);
}
const _lower = (s) => String(s == null ? '' : s).trim().toLowerCase();
const _nameUnitKey = (e) => 'n:' + _lower(e && e.label) + '|' + _lower(e && e.unit);

/**
 * The key one delivery line is set to "NOT A COST" under
 * (projects.other_delivery_excluded) — and, unlike otherItemKey, it never moves
 * with the catalogue: it is made only of what the line itself carries.
 *
 * An entry known by its NAME alone (`byName`: an older extra, written before the
 * catalogue id was stored — 66-jobcosts.js _jcEstimateDelivery) answers by that
 * name and unit, whatever row or rows the name fits today. Every other line
 * carries its one catalogue row and answers by it. So a stored exclusion is a
 * fact about the line: no catalogue edit on another screen, and no other line of
 * the job, can put its money back or take somebody else's out — today or in an
 * as-of run. (The task RULE of the same item moves money between tasks, never in
 * or out of the job, and keeps its adoption below.)
 */
export function excludedKey(e) {
  return e && e.byName ? _nameUnitKey(e) : otherItemKey(e);
}

/**
 * A rule keeps working when the CATALOGUE changes under it.
 *
 * otherItemKey reads the catalogue as it is today, so the same delivery line
 * changes key when somebody edits the catalogue on another screen: an older
 * extra (it carries a name, no id) is 'id:<row>' while one row has that name
 * and 'n:<name>|<unit>' from the day a second row is given it — and back when
 * the name is unique again. The rule on file stays under the key it was picked
 * in, and without this the item drifted back to General with no word said.
 *
 * So a line with no rule under its own key takes the rule left under its OTHER
 * form: name|unit for a line that means one row, each of its rows (in id
 * order) for a line whose name fits several. Only an ORPHAN is taken — a key no
 * line of the job answers to any more. A rule another row of the list shows as
 * its own is that row's, and is never borrowed.
 *
 * `lists` is buildTaskOverrideList's map; `lines` every other-item line of the
 * job ({ ids, label, unit }), priced or not. Returns the lists with the taken
 * rules under the lines' own keys, and `adopted` { own key: the key it came
 * from } — what a new pick on that row has to clear, or the old rule would
 * come back the moment the row is set to General.
 */
export function adoptItemRules(lists, lines) {
  const ref = (k) => itemRef('other_item', k);
  const has = (k) => ((lists || {})[ref(k)] || []).length > 0;
  const own = new Set((lines || []).map(otherItemKey));
  const out = { ...(lists || {}) }, adopted = {};
  (lines || []).forEach(l => {
    const key = otherItemKey(l);
    if (has(key) || adopted[key]) return;
    const ids = (l && l.ids) || [];
    const from = (ids.length === 1 ? [_nameUnitKey(l)] : ids.slice().sort().map(id => 'id:' + id))
      .find(a => !own.has(a) && has(a));
    if (from) { out[ref(key)] = lists[ref(from)]; adopted[key] = from; }
  });
  return { lists: out, adopted };
}

// (buildTaskOverrideMap, the one-task-per-key map, died with the split
// feature: a key now holds a LIST, and collapsing it would show a random
// member as "the" answer.)

/**
 * project_cost_tasks rows → one LIST of split rows per line key. A line's
 * assignment is no longer a single task: it is a set of rows, each naming a
 * task, an optional date window, and a share. The single-task case is a
 * one-row list with null dates and share 1 — which is exactly what every
 * pre-split row in the table already is.
 */
export function buildTaskOverrideList(rows) {
  const map = {};
  (rows || []).forEach(r => {
    if (!r || !r.source) return;
    const k = itemRef(r.source, r.source_ref);
    (map[k] = map[k] || []).push({
      taskId: r.task_id,
      fromDate: r.from_date || null,
      toDate: r.to_date || null,
      share: r.share == null ? 1 : Number(r.share),
    });
  });
  return map;
}

/** The single-answer view of a list: one undated full-share row, else null. */
export function soleFullAssignment(list) {
  if (!list || list.length !== 1) return null;
  const r = list[0];
  if (r.fromDate || r.toDate || Number(r.share ?? 1) < 1) return null;
  return r.taskId ?? null;
}

const _dayCount = (a, b) =>
  Math.round((new Date(b + 'T00:00:00') - new Date(a + 'T00:00:00')) / 86400000);

/**
 * What fraction of a line's period one split row covers. Within one
 * allocation, quantity and rate are constant, so days and dollars are
 * proportional — that linearity is what makes a date split exact. A row
 * with no dates, or a line with no period, is the whole line.
 */
export function windowFraction(row, periodStart, periodEnd) {
  if (!periodStart || !periodEnd) return 1;
  if (!row.fromDate && !row.toDate) return 1;
  const total = _dayCount(periodStart, periodEnd);
  if (total <= 0) return 1;
  const from = row.fromDate && row.fromDate > periodStart ? row.fromDate : periodStart;
  const to = row.toDate && row.toDate < periodEnd ? row.toDate : periodEnd;
  const days = _dayCount(from, to);
  return Math.max(0, Math.min(1, days / total));
}

/**
 * No single day may be claimed for more than 100% of the line. A boundary
 * sweep is exact here because shares are constant inside a window: the sum
 * can only change where a window starts or ends. A line with no period has
 * no days to sweep — its shares simply sum.
 */
export function validateSplitWindows(rows, periodStart, periodEnd) {
  const list = (rows || []).map(r => ({
    from: r.fromDate || null, to: r.toDate || null,
    share: r.share == null ? 1 : Number(r.share) }));
  if (!periodStart || !periodEnd) {
    const sum = list.reduce((s, r) => s + r.share, 0);
    return sum > 1.000001 ? { ok: false, at: null, sum } : { ok: true };
  }
  const pts = [...new Set(list.flatMap(r => [r.from || periodStart, r.to || periodEnd])
    .concat([periodStart, periodEnd]))].sort();
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i], b = pts[i + 1];
    if (a >= b) continue;
    const sum = list.reduce((s, r) =>
      ((r.from || periodStart) <= a && (r.to || periodEnd) >= b) ? s + r.share : s, 0);
    if (sum > 1.000001) return { ok: false, at: a, sum };
  }
  return { ok: true };
}

/**
 * The zero-click defaults: a quantity contract line already names its
 * production item, so a recipe cost for that item belongs to that task
 * unless somebody says otherwise. First line wins on a duplicate — stable,
 * and the override table exists for the exceptions.
 */
export function buildDerivedTaskMap(contractLines) {
  const map = {};
  (contractLines || []).forEach(l => {
    if (!l || l.earn_method !== 'quantity' || !l.material_type_id) return;
    if (!(l.material_type_id in map)) map[l.material_type_id] = l.id;
  });
  return map;
}

// The Contract tab's order (sort_order, then created_at; a tie keeps the order
// the lines came in) — what "the first task" means everywhere below.
const _contractOrder = (a, b) =>
  (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0) ||
  String(a.created_at || '').localeCompare(String(b.created_at || ''));

/**
 * The zero-click task of a DELIVERED item (2026-11-02, material = what came
 * in): the task whose recipe names the catalogue item — a quantity contract
 * line claims its production item (buildDerivedTaskMap), and that item's
 * recipe lines name their components — else the job's only task. An item two
 * recipes on two tasks name goes to ONE of them, the first by contract-line
 * order; the admin moves it with a per-item rule.
 *
 * Returns { byItem: { [delivery_material_id]: task id }, rank: { [task id]: n },
 * only: the task id of a job with exactly one task, else null }. A typed recipe
 * component (custom_name) has no catalogue row and names nothing.
 */
export function itemTaskDefaults(recipeLines, contractLines) {
  const lines = (contractLines || []).filter(Boolean).slice().sort(_contractOrder);
  const rank = {};
  lines.forEach((l, i) => { rank[l.id] = i; });
  const byType = buildDerivedTaskMap(lines);
  const byItem = {};
  (recipeLines || []).forEach(r => {
    if (!r || r.custom_name != null || !r.delivery_material_id) return;
    const t = byType[r.material_type_id];
    if (t == null) return;
    const cur = byItem[r.delivery_material_id];
    if (cur == null || rank[t] < rank[cur]) byItem[r.delivery_material_id] = t;
  });
  return { byItem, rank, only: lines.length === 1 ? lines[0].id : null };
}

/**
 * itemTaskDefaults' answer for one delivery line — `ids` are the catalogue
 * rows it can mean (an older extra known by name may fit several: the first
 * task by contract order wins). A line with no catalogue row has no default.
 */
export function defaultItemTask(ids, defaults) {
  if (!defaults || !(ids || []).length) return null;
  const hits = ids.map(id => defaults.byItem[id]).filter(t => t != null);
  if (hits.length) return hits.reduce((a, b) => (defaults.rank[b] < defaults.rank[a] ? b : a));
  return defaults.only ?? null;
}

/**
 * Which task one catalogue item of a project files under, as one answer:
 * (1) the item's own rule on the project (project_cost_tasks, source
 * 'other_item'), (2) the task whose recipe names it, (3) the job's only task,
 * (4) null — General. Asked of resolveItemShares, the function the money is
 * filed by, so a screen showing this cannot disagree with Job Costs. A rule
 * split across tasks has no one task: null here, the shares from
 * resolveItemShares. (A delivery LINE can also be filed by hand on Job Costs,
 * for that line or its whole order — those come before the defaults there.)
 */
export function taskForItem(item, ctx) {
  const { projectId, deliveryMaterialId } = item || {};
  const c = ctx || {};
  const mine = (r) => !!r && (projectId == null || r.project_id == null || r.project_id === projectId);
  const lines = (c.contractLines || []).filter(mine);
  const rules = (c.itemRules || []).filter(r => mine(r) && (r.source == null || r.source === 'other_item'));
  return resolveItemTask({ source: 'delivery_item', ref: '', itemKey: 'id:' + deliveryMaterialId,
    ids: deliveryMaterialId ? [deliveryMaterialId] : [], amount: 1 }, {
    overrides: buildTaskOverrideList(rules.map(r => ({ ...r, source: 'other_item' }))),
    itemTasks: itemTaskDefaults((c.recipeLines || []).filter(mine), lines),
    validTaskIds: lines.map(l => l.id),
  });
}

/**
 * Deliveries priced per item explode into one sub-item per priced line, so
 * an order carrying block AND brick material can be filed item by item. An
 * order priced by its supplier invoice does NOT explode — the invoice is one
 * amount, and splitting it would invent a breakdown the paper doesn't have.
 * Conservation is structural: est.total is defined as the sum of its priced
 * lines and est.freight as the sum of their freight, so the explosion always
 * re-adds to the order row's amount (the total before freight).
 * `topicFrom` keeps every sub-item under the topic the WHOLE order resolves
 * to — the topic axis knows nothing about the split.
 */
export function expandForTasks(items, estById) {
  const out = [];
  (items || []).forEach(item => {
    // Labour is priced by trade (one row, one loaded rate) but filed by task
    // via the timesheet's own field entry — a second, independent breakdown
    // of the SAME amount, not a re-derivation of it (see 66-jobcosts.js
    // _jcComputeLabour). A trade with no tasked hours carries no breakdown at
    // all, so it passes through exactly as before this feature existed.
    if (item.source === 'labour_trade' && item.taskBreakdown && item.taskBreakdown.length) {
      const parentTopicRef = itemRef('labour_trade', item.ref);
      item.taskBreakdown.forEach(b => out.push({
        source: 'labour_task',
        ref: item.ref + '#' + (b.taskId || 'general'),
        parentRef: item.ref,
        taskId: b.taskId != null ? b.taskId : null,
        amount: Number(b.cost) || 0,
        topicFrom: parentTopicRef,
      }));
      return;
    }
    // Sales tax is ONE line on the statement and stays one — but it was charged
    // on material, and material belongs to tasks. `taxBreakdown` says how much
    // of the tax each piece of taxed material carries ({ source, ref,
    // parentRef, amount }, built in 66-jobcosts.js _jcTopicItems), and each
    // part is filed wherever that material is (resolveItemShares, 'tax_part').
    // Before this the whole tax sat on whichever single task the line was
    // filed under, so on a job with two products one of them carried the
    // other's tax in its unit cost. The LAST part takes whatever the others
    // left, so the parts cannot drift from the line's own amount.
    //
    // The FREIGHT row of a delivery is the same kind of line (2026-10-02): the
    // truck's share of each item's landed price files under Trucking, as one
    // row per order, and each item's share goes where that item is filed. It
    // carries `followBreakdown` instead — same shape, same expansion; only the
    // parent the part falls back to differs (`parentSource`).
    const parts = item.source === 'tax' ? item.taxBreakdown : item.followBreakdown;
    if (parts && parts.length) {
      const parentTopicRef = itemRef(item.source, item.ref);
      let left = Number(item.amount) || 0;
      parts.forEach((b, n) => {
        const amount = n === parts.length - 1 ? left : (Number(b.amount) || 0);
        left -= amount;
        out.push({
          source: 'tax_part',
          ref: item.ref + '#' + b.source + '|' + b.ref,
          parentRef: item.ref,
          parentSource: item.source,
          of: { source: b.source, ref: b.ref, parentRef: b.parentRef, itemKey: b.itemKey, ids: b.ids },
          amount,
          topicFrom: parentTopicRef,
        });
      });
      return;
    }
    if (item.source !== 'delivery' || item.invoiced || item.voided) { out.push(item); return; }
    const est = (estById || {})[item.ref];
    const lines = est && est.lines;
    if (!lines || !lines.length) { out.push(item); return; }
    const parentTopicRef = itemRef('delivery', item.ref);
    // Each item at its price BEFORE freight: l.cost is landed, and the freight
    // inside it is the order's own freight row. The items then re-add to the
    // order row's amount (est.total − est.freight), not to est.total.
    lines.forEach(l => out.push({
      source: 'delivery_item',
      ref: deliveryItemRef(item.ref, l.key),
      parentRef: item.ref,
      itemKey: otherItemKey(l),
      ids: l.ids || [],
      amount: (Number(l.cost) || 0) - (Number(l.freight) || 0),
      topicFrom: parentTopicRef,
    }));
  });
  return out;
}

/**
 * The money of one item, task by task: [{taskId, amount}]. Same resolution
 * order as ever (own answer, then the parent's, then derived, then General),
 * but each answer is now a LIST of rows, each taking
 *   amount × windowFraction(row, item.periodStart, item.periodEnd) × share.
 * Whatever the rows do not cover is the caller's General — computed THERE as
 * the remainder, never here, so a bad split can misfire visibly but can
 * never invent money.
 *
 * A labour_task child answers for its own bucket first (the timesheet row it
 * came from named a task); a task-less bucket, or one whose task was since
 * deleted, falls to whatever the whole trade row is filed under — the SAME
 * answer a plain labour_trade item (no breakdown) would give.
 */
export function resolveItemShares(item, opts) {
  const o = opts || {};
  const valid = o.validTaskIds instanceof Set ? o.validTaskIds : new Set(o.validTaskIds || []);
  const ok = id => id != null && valid.has(id);
  if (!item) return [];
  const amount = Number(item.amount) || 0;
  const spread = (list) => {
    const rows = (list || []).filter(r => ok(r.taskId));
    if (!rows.length) return null;
    return rows.map(r => ({
      taskId: r.taskId,
      amount: amount * windowFraction(r, item.periodStart, item.periodEnd)
                     * (r.share == null ? 1 : Number(r.share)),
    }));
  };
  if (item.source === 'entry') return ok(item.taskId) ? [{ taskId: item.taskId, amount }] : [];
  const lists = o.overrides || {};
  if (item.source === 'labour_task') {
    if (ok(item.taskId)) return [{ taskId: item.taskId, amount }];
    return spread(lists[itemRef('labour_trade', item.parentRef)]) || [];
  }
  if (item.source === 'delivery_item') {
    // Most specific first: this line of this delivery, then the item's rule
    // for the job (every delivery of it), then the whole order. An item with
    // no rule follows its order's filing, as it always did. Nothing filed by
    // hand: the item's default (o.itemTasks — itemTaskDefaults: the task whose
    // recipe names it, else the job's only task), else General.
    const d = defaultItemTask(item.ids, o.itemTasks);
    return spread(lists[itemRef('delivery_item', item.ref)])
        || spread(lists[itemRef('other_item', item.itemKey)])
        || spread(lists[itemRef('delivery', item.parentRef)])
        || (ok(d) ? [{ taskId: d, amount }] : []);
  }
  if (item.source === 'tax_part') {
    // The tax on one piece of material goes wherever THAT material's money
    // goes — same rows, same shares, asked of the same function. Only when the
    // material has no task at all does the tax line's own assignment apply
    // (every assignment made before the tax was split is exactly that row).
    // A delivery's freight row follows its items the same way (parentSource).
    // `of.itemKey` rides along, so both follow the item's rule too.
    const like = resolveItemShares({ ...item.of, amount }, o);
    return like.length ? like
      : (spread(lists[itemRef(item.parentSource || 'tax', item.parentRef)]) || []);
  }
  const own = spread(lists[itemRef(item.source, item.ref)]);
  if (own) return own;
  if (item.source === 'recipe') {
    // A redirected slice's ref is '<material_type_id>#<topic>' — the derived
    // default (a quantity contract line claiming its production item) applies
    // to every slice of that item, so the lookup uses the id half.
    const d = (o.derived || {})[String(item.ref).split('#')[0]];
    return ok(d) ? [{ taskId: d, amount }] : [];
  }
  return [];
}

/** The single-answer view: every share on one task → that task, else null. */
export function resolveItemTask(item, opts) {
  const rows = resolveItemShares(item, opts);
  if (!rows.length) return null;
  const t = rows[0].taskId;
  return rows.every(r => r.taskId === t) ? t : null;
}

/**
 * Turn a batch of "file this line under that task" answers into the writes
 * the database will actually accept.
 *
 * The trap this exists for: a statement ROW is not a unique assignment KEY.
 * Scaffold is filed by the equipment NAME (equipment_locations churns its id
 * every time gear moves, the name is what survives — see 66-jobcosts.js
 * _jcTopicItems), so the same piece allocated over two date ranges is two
 * rows on screen sharing ONE (source, source_ref). Sending both in a single
 * upsert is how you get Postgres's "ON CONFLICT DO UPDATE command cannot
 * affect row a second time" — the whole batch fails, nothing saves.
 *
 * So the key here is (source, ref), never the row: rows that share a key are
 * the same thing and file together, which is exactly how the topic axis has
 * always treated them. Last answer wins on a collision, and the UI is
 * expected to keep sibling rows showing the same value rather than let the
 * owner believe he set two different tasks.
 *
 * Typed costs carry their task on their own row (job_cost_entries.task_id),
 * so they come back grouped by task for one update each instead of one per
 * cost. Answers with no task (null/'') are dropped: "leave it" is not a write.
 */
export function planTaskWrites(assignments) {
  const byKey = new Map();
  (assignments || []).forEach(a => {
    if (!a || !a.source || !a.taskId) return;
    byKey.set(itemRef(a.source, a.ref), a);
  });
  const overrides = [];
  const entryIdsByTask = {};
  byKey.forEach(a => {
    if (a.source === 'entry') {
      (entryIdsByTask[a.taskId] = entryIdsByTask[a.taskId] || []).push(a.ref);
    } else {
      overrides.push({ source: a.source, source_ref: a.ref, task_id: a.taskId });
    }
  });
  return { overrides, entryIdsByTask };
}

/**
 * File every line under (task, topic) and add up each cell.
 * `resolvedTopics` is the topic roll's `resolved` map — the topic axis stays
 * the single answer it already is; this roll only adds the second axis.
 *
 * `rows` folds the same answers back up to the STATEMENT row each item came
 * from, so a screen can say what one row's state is without resolving
 * anything a second (possibly drifting) way. The key is the parent row's ref,
 * which is what `item.topicFrom || itemRef(source, ref)` already means — the
 * expression the topic lookup above is built on. Because every expanded item
 * belongs to exactly one row, the sum of `rows[*].unfiled` IS `generalTotal`;
 * that identity is what lets a filtered statement print its own subtotals.
 * `byTask` is the row's money per task, so a screen showing one task's costs
 * (Job Costs' task detail) prints each row's share of it.
 */
export function rollUpTasks(items, opts) {
  const o = opts || {};
  const resolvedTopics = o.resolvedTopics || {};
  const cells = {}, taskTotals = {}, general = {}, resolvedTasks = {}, rows = {};
  let generalTotal = 0, grand = 0;
  (items || []).forEach(item => {
    const amount = Number(item && item.amount) || 0;
    const rowRef = item.topicFrom || itemRef(item.source, item.ref);
    const topic = resolvedTopics[rowRef] || 'other';
    const shares = resolveItemShares(item, o);
    grand += amount;
    const row = rows[rowRef] || (rows[rowRef] = { taskIds: [], unfiled: 0, total: 0, byTask: {} });
    row.total += amount;
    let filed = 0;
    shares.forEach(s => {
      filed += s.amount;
      if (!cells[s.taskId]) cells[s.taskId] = {};
      cells[s.taskId][topic] = (cells[s.taskId][topic] || 0) + s.amount;
      taskTotals[s.taskId] = (taskTotals[s.taskId] || 0) + s.amount;
      if (!row.taskIds.includes(s.taskId)) row.taskIds.push(s.taskId);
      row.byTask[s.taskId] = (row.byTask[s.taskId] || 0) + s.amount;
    });
    // General IS the remainder — never its own computation. Rounding crumbs
    // and mis-splits land here visibly instead of anywhere silently, which
    // is what keeps cells + General === grand true by construction.
    const rest = amount - filed;
    if (Math.abs(rest) > 1e-9) {
      general[topic] = (general[topic] || 0) + rest;
      generalTotal += rest;
      row.unfiled += rest;
    }
    const one = shares.length > 0 && shares.every(s => s.taskId === shares[0].taskId);
    resolvedTasks[itemRef(item.source, item.ref)] =
      one && Math.abs(rest) <= 0.005 ? shares[0].taskId : null;
  });
  return { cells, taskTotals, general, generalTotal, resolvedTasks, rows, grand };
}

// ── Cents: decided once, here, and never by a renderer ───────────────────────
// Every figure above is an unrounded float (days × monthly / 30.4375, quantity
// × a long unit cost, direct × the overhead %), and each screen used to round
// each cell on its own — so a column could miss its own total by a cent. The
// rule from here down: the TOTAL is the printed fact, and the rows under it are
// fitted to it. Never the other way round, and never a sum of rounded cells.

/**
 * To whole cents, half AWAY from zero — the same on both sides of zero, so a
 * cost and its exact reversal round to the same cent and cancel. Math.round on
 * its own rounds −x.5 toward zero and +x.5 away from it. "+ 0" turns a
 * negative zero into plain 0. Same rounding as job-value.js roundMoney.
 */
export const toCents = v => {
  const n = Number(v) || 0;
  return Math.sign(n) * Math.round(Math.abs(n) * 100) / 100 + 0;
};

/**
 * {key: dollars} → the same keys in whole cents that add up to `total` (at the
 * cent) EXACTLY. Each row rounds to its nearest cent; whatever cents are then
 * missing or spare go to the rows rounding moved the furthest (largest
 * remainder), so no row ends up more than a cent from its own value.
 *
 * A gap bigger than one cent per row is not rounding — it is a feed that
 * reached one sum and not the other. Forcing THAT to foot would hide a wiring
 * bug inside a plausible-looking row, so the rows are returned as they round
 * and the caller's own guard reports the drift.
 */
export function footToCents(map, total) {
  const keys = Object.keys(map || {});
  const exact = keys.map(k => (Number(map[k]) || 0) * 100);
  const cents = exact.map(Math.round);
  const left = Math.round((Number(total) || 0) * 100) - cents.reduce((s, c) => s + c, 0);
  // A row holding nothing never takes a cent: "$0.01" on an empty group is a
  // claim, not a rounding. "Nothing" includes a float crumb — the difference of
  // two cumulatives of the same money (a period window) can be 1e-13 instead
  // of 0, and that is still a row nothing happened in.
  const movable = keys.map((_, i) => i).filter(i => Math.abs(exact[i]) > 1e-7);
  if (left !== 0 && Math.abs(left) <= movable.length) {
    const step = left > 0 ? 1 : -1;
    movable
      .sort((a, b) => step * ((exact[b] - cents[b]) - (exact[a] - cents[a])) || a - b)
      .slice(0, Math.abs(left))
      .forEach(i => { cents[i] += step; });
  }
  const out = {};
  // "+ 0": a negative crumb rounds to −0, and −0 prints as "-$0.00".
  keys.forEach((k, i) => { out[k] = cents[i] / 100 + 0; });
  return out;
}

// No task id can be this, so General rides through footToCents as one more row.
const GENERAL_KEY = '\u0000general';

/**
 * What each task COST once it carries its share of overhead — the figure the
 * By-task views print, so the task margins add up to the job's profit.
 *
 * Overhead is one org-wide % of direct cost, so a task's share is arithmetic,
 * not an allocation choice: direct × (1 + pct). General (cost nobody filed
 * under a task) carries its own share the same way. The result is fitted to
 * `totalSpent` in cents, so costByTask + generalCost === totalSpent exactly.
 *
 * `taskRoll` is READ, never changed: unit-cost.js multiplies the direct
 * taskTotals by (1 + pct) itself, and the two drift guards compare
 * taskRoll.grand against DIRECT cost. Loading the roll in place would charge
 * overhead twice.
 *
 * `generalCost` is exactly 0 whenever direct General is within half a cent of
 * nothing — a fully-filed job never grows a "$0.00 General" row out of float
 * crumbs, and its tasks foot to the total on their own.
 */
export function loadTaskCosts(taskRoll, overheadPct, totalSpent) {
  const roll = taskRoll || {};
  const mult = 1 + (Number(overheadPct) || 0);
  const raw = {};
  Object.keys(roll.taskTotals || {}).forEach(id => {
    raw[id] = (Number(roll.taskTotals[id]) || 0) * mult;
  });
  const general = Number(roll.generalTotal) || 0;
  if (Math.abs(general) > 0.005) raw[GENERAL_KEY] = general * mult;
  const costByTask = footToCents(raw, totalSpent);
  const generalCost = costByTask[GENERAL_KEY] || 0;
  delete costByTask[GENERAL_KEY];
  return { costByTask, generalCost };
}

/**
 * The By-task table, as every surface prints it: Job Costs, the P&L statement
 * (job to date and each half of a period), its PDF and its CSV. One builder so
 * the five of them cannot disagree about a cell.
 *
 *   rows     one per contract line, in the order the Contract tab shows them
 *            (sort_order, then created_at). `earned`/`margin` are null — an em
 *            dash, never $0 — for a line with no sell price on file.
 *   general  cost filed under no task, or null when there is none to show.
 *   total    the ENGINE's totals (totalCost / totalEarned), not a sum of the
 *            cells above — so it is the same number as Total cost and Profit.
 *
 * Every cell is in cents and each margin is its own printed earned minus its
 * own printed cost, so a row always subtracts on a calculator. Both money
 * columns are fitted to their total (cost by loadTaskCosts, earned here), so
 * each column adds up too — and with it the margins: row margins minus General
 * equal the total row's.
 */
export function taskTable(o) {
  const opts = o || {};
  const unpriced = opts.unpricedIds instanceof Set ? opts.unpricedIds : new Set(opts.unpricedIds || []);
  const earnedBy = opts.earnedByLineId || {};
  const costBy = opts.costByTask || {};
  const lines = (opts.lines || []).slice().sort(_contractOrder);
  const priced = new Map();
  lines.forEach(l => {
    if (!unpriced.has(l.id) && earnedBy[l.id] != null) priced.set(l.id, earnedBy[l.id]);
  });
  const general = toCents(opts.generalCost);
  const cost = toCents(opts.totalCost);
  const earned = priced.size ? toCents(opts.totalEarned) : null;
  const earnedFit = footToCents(Object.fromEntries(priced), earned);
  const rows = lines.map(line => {
    const c = toCents(costBy[line.id]);
    const e = priced.has(line.id) ? earnedFit[line.id] : null;
    return { line, earned: e, cost: c, margin: e == null ? null : toCents(e - c) };
  });
  return {
    rows,
    general: general === 0 ? null : general,
    total: { earned, cost, margin: earned == null ? null : toCents(earned - cost) },
  };
}

/**
 * The same table with no Profit figure anywhere — every row margin and the
 * total's set to null, which each surface already prints as an em dash with no
 * loss rail. Earned and cost are untouched, so both columns still add up.
 *
 * For a job with no revenue on file yet: its headline withholds Profit ("earned
 * $0 minus cost" is a contract nobody has typed, not a loss), so the By-task
 * table under that headline must not print the same minus-cost in red. The
 * FLAG is the caller's — whichever one its own headline reads (Job Costs: the
 * job's hasContract; the P&L statement, its PDF and CSV: the group's, so a
 * mother whose sub-projects carry the contract keeps her figures). The engine
 * builds taskTable without it because it cannot know the group.
 */
export function dashProfit(table, hasRevenue) {
  if (hasRevenue || !table) return table;
  return {
    ...table,
    rows: table.rows.map(r => ({ ...r, margin: null })),
    total: { ...table.total, margin: null },
  };
}
